<template>
  <app-layout>
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        学校管理 登録タスク一覧
      </h2>
    </template>

    <transition name="notification">
      <div
        v-if="showLocalMessage && localMessage"
        class="fixed top-0 z-50 flex items-center justify-between w-full opacity-95"
        :class="{
          'bg-green-500': localMessage.type === 'success',
          'bg-red-400': localMessage.type === 'error',
        }">
        <div class="flex items-center">
          <svg
            class="ml-4 mr-2 flex-shrink-0 w-4 h-4 fill-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20">
            <polygon
              v-if="localMessage.type === 'success'"
              fill="white"
              points="0 11 2 9 7 14 18 3 20 5 7 18" />
            <path
              v-else
              d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm1.41-1.41A8 8 0 1 0 15.66 4.34 8 8 0 0 0 4.34 15.66zm9.9-8.49L11.41 10l2.83 2.83-1.41 1.41L10 11.41l-2.83 2.83-1.41-1.41L8.59 10 5.76 7.17l1.41-1.41L10 8.59l2.83-2.83 1.41 1.41z" />
          </svg>
          <div class="py-4 text-white font-bold">
            {{ localMessage.text }}
          </div>
        </div>
        <button
          type="button"
          class="group mr-2 p-2"
          @click="showLocalMessage = false">
          <svg
            class="block w-2 h-2 group-hover:fill-white"
            :class="{
              'fill-green-800': localMessage.type === 'success',
              'fill-red-800': localMessage.type === 'error',
            }"
            xmlns="http://www.w3.org/2000/svg"
            width="235.908"
            height="235.908"
            viewBox="278.046 126.846 235.908 235.908">
            <path
              fill="white"
              d="M506.784 134.017c-9.56-9.56-25.06-9.56-34.62 0L396 210.18l-76.164-76.164c-9.56-9.56-25.06-9.56-34.62 0-9.56 9.56-9.56 25.06 0 34.62L361.38 244.8l-76.164 76.165c-9.56 9.56-9.56 25.06 0 34.62 9.56 9.56 25.06 9.56 34.62 0L396 279.42l76.164 76.165c9.56 9.56 25.06 9.56 34.62 0 9.56-9.56 9.56-25.06 0-34.62L430.62 244.8l76.164-76.163c9.56-9.56 9.56-25.06 0-34.62z" />
          </svg>
        </button>
      </div>
    </transition>

    <div class="flex justify-left pl-12 pt-4 my-2">
      <a
        class="py-2 px-6 bg-blue-600 hover:shadow-lg text-white rounded"
        :href="route('usersRegistration')">登録画面へ</a>
    </div>

    <div
      v-if="!userRegistrationTask.length"
      class="text-center">
      タスクはありません。
    </div>
    <div
      v-else
      class="py-4 flex flex-col items-center">
      <div class="shadow w-11/12 overflow-x-auto overflow-y-auto border-b border-gray-200 sm:rounded-lg">
        <table class="w-full table-auto divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                学校名
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                CSVファイル名
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                ステータス
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                処理したユーザ数
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
                登録実行日時
              </th>
              <th
                scope="col"
                class="px-6 py-3 text-center font-medium text-gray-500 uppercase tracking-wider">
              </th>
            </tr>
          </thead>
          <tbody
            v-for="(item, i) in userRegistrationTask"
            :key="i"
            class="bg-white divide-y divide-gray-200">
            <tr>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="text-gray-900">
                    {{ item.id }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <div class="text-gray-900">
                  {{ getSchoolName(item.school_id) ?? '一般ユーザ' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <span class="text-gray-900">
                  {{ item.csv_file_name }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <span
                  class="px-2 inline-flex leading-5 rounded-full"
                  :class="{
                    'text-yellow-600': item.status == 1,
                    'text-green-600': item.status == 2,
                    'text-red-600': item.status == 3,
                  }">
                  {{ getTaskStatus(item.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                  {{ item.complete_users_num }} / {{ item.users_num }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-center">
                <span class="px-2 inline-flex leading-5 rounded-full text-gray-900">
                  {{ formatDate(item.created_at) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <a
                  class="py-2 px-6 text-blue-600"
                  :href="route('usersRegistration.task.show', { 'userRegistrationTask': item.id })">登録タスク一覧</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </app-layout>
</template>

<script setup>
import {
  ref, onMounted, onUnmounted, computed,
} from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { usePage } from '@inertiajs/inertia-vue3'
import moment from 'moment'
import formatDate from '@/actions/format/date'
import AppLayout from '@/Layouts/AppLayout.vue'

const props = defineProps({
  schools: {
    type: Array,
    default: () => [],
  },
  userRegistrationTask: {
    type: Array,
    default: () => [],
  },
})

const getSchoolName = (id) => props.schools.find(e => e.id == id)?.name

const getTaskStatus = (status) => {
  switch (status) {
    case 1: return '処理中'
    case 2: return '完了'
    case 3: return '失敗'
    default: return ''
  }
}

const hasInProgress = computed(() => props.userRegistrationTask.some(item => item.status === 1))

let reloadInterval = null
let previousStatuses = new Map()

const snapshotStatuses = () => {
  const map = new Map()
  props.userRegistrationTask.forEach(task => {
    map.set(task.id, task.status)
  })
  return map
}

const clearFlash = () => {
  const { flash } = usePage().props.value
  if (flash) {
    flash.success = null
    flash.error = null
    flash.message = null
  }
}

const localMessage = ref(null)
const showLocalMessage = ref(false)
let localMessageTimeout = null

const showNotification = (type, text) => {
  if (localMessageTimeout) clearTimeout(localMessageTimeout)
  localMessage.value = { type, text }
  showLocalMessage.value = true
  localMessageTimeout = setTimeout(() => {
    showLocalMessage.value = false
  }, 5000)
}

const stopPolling = () => {
  if (reloadInterval) {
    clearInterval(reloadInterval)
    reloadInterval = null
  }
}

onMounted(() => {
  if (hasInProgress.value) {
    previousStatuses = snapshotStatuses()
    reloadInterval = setInterval(() => {
      clearFlash()
      previousStatuses = snapshotStatuses()
      Inertia.reload({
        only: ['userRegistrationTask'],
        onSuccess: () => {
          props.userRegistrationTask.forEach(task => {
            const prev = previousStatuses.get(task.id)
            if (prev === 1 && task.status === 2) {
              showNotification('success', 'CSV取込処理が完了しました')
            }
            if (prev === 1 && task.status === 3) {
              showNotification('error', 'CSV取込処理に失敗しました')
            }
          })
          if (!hasInProgress.value) {
            stopPolling()
          }
        },
      })
    }, 5000)
  }
})

onUnmounted(() => {
  stopPolling()
  if (localMessageTimeout) clearTimeout(localMessageTimeout)
})

</script>

<style scoped>
.notification-enter-from {
  opacity: 0;
}
.notification-enter-active {
  transition: opacity 0.7s;
}
.notification-enter-to {
  opacity: 0.95;
}
.notification-leave-from {
  opacity: 0.95;
}
.notification-leave-active {
  transition: opacity 0.7s;
}
.notification-leave-to {
  opacity: 0;
}
</style>
